/**
 * Crawlable JSON-LD + absolute URLs for Everywhere hub / A–Z / PDP.
 * Names and FAQ copy come from the live catalog + the same FAQ tuples
 * rendered in the DOM — never invented SKUs or turnaround promises.
 */
const config = require('../../utils/constants/siteConfig');

export const EVERYWHERE_OG_IMAGE_PATH = '/everywhere/og-image.jpg';

const siteOrigin = (): string =>
  String(config.siteUrl || '').replace(/\/$/, '');

/** Absolute URL for a site-relative path. Page paths get a trailing slash. */
export const everywhereAbsoluteUrl = (path: string): string => {
  if (/^https?:\/\//i.test(path)) return path;
  const origin = siteOrigin();
  const normalized = path.startsWith('/') ? path : `/${path}`;
  const [pathname, suffix = ''] = normalized.split(/(?=[?#])/);
  const isFile = /\.[a-z0-9]+$/i.test(pathname);
  const withSlash =
    isFile || pathname.endsWith('/') ? pathname : `${pathname}/`;
  return `${origin}${withSlash}${suffix}`;
};

export const EVERYWHERE_OG_IMAGE_URL = everywhereAbsoluteUrl(
  EVERYWHERE_OG_IMAGE_PATH,
);

export type BreadcrumbCrumb = {
  name: string;
  path: string;
};

export const HUB_CRUMBS: BreadcrumbCrumb[] = [
  { name: 'Lab tests', path: '/everywhere/' },
];

export const azCrumbs = (): BreadcrumbCrumb[] => [
  ...HUB_CRUMBS,
  { name: 'All lab tests', path: '/everywhere/tests/' },
];

export const pdpCrumbs = (name: string, urlSlug: string): BreadcrumbCrumb[] => [
  ...azCrumbs(),
  { name, path: `/everywhere/tests/${urlSlug}/` },
];

export const everywhereBreadcrumbList = (
  crumbs: BreadcrumbCrumb[],
): Record<string, unknown> => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: crumbs
    .filter((crumb) => crumb.name.trim())
    .map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name.trim(),
      item: everywhereAbsoluteUrl(crumb.path),
    })),
});

/** FAQPage JSON-LD from the exact [question, answer] tuples shown in the DOM. */
export const everywhereFaqPage = (
  items: Array<[string, string]>,
): Record<string, unknown> => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: items.map(([question, answer]) => ({
    '@type': 'Question',
    name: question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: answer,
    },
  })),
});
